import { useState } from 'react';
import { toast } from 'react-toastify';
import { Alert, Button, Pagination } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { useFranchisors, useAttachFranchiseMutation, useDetachFranchiseMutation } from '../hooks/useFranchises';
import { FranchiseMembersTable } from './FranchiseMembersTable';
import { FranchisePartnerSelect } from './FranchisePartnerSelect';
import { isApiError } from '@/services/api';
import type { Dealer } from '../types/dealers.types';

interface Props {
    dealerId: string;
    dealerName: string;
}

/**
 * OEMs tab: this page is a franchise_dealer; lists the OEMs franchising it,
 * with attach (pick an oem) and detach. Mirrors FranchisesTab with the
 * franchise-dealer/oem roles swapped.
 *
 * Layout follows the shared "Add New / Attach" pattern (see DealerContacts):
 * the table sits on top and the attach affordance lives in a panel below it,
 * toggling between an Attach button and an inline picker (no modal).
 */
export function OemsTab({ dealerId, dealerName }: Props) {
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [attaching, setAttaching] = useState(false);
    const [pickedOem, setPickedOem] = useState<Dealer | null>(null);
    const [attachError, setAttachError] = useState<string | null>(null);
    const [detachingId, setDetachingId] = useState<string | null>(null);

    const { data, isLoading, isError } = useFranchisors(dealerId, page, perPage);
    const attachMutation = useAttachFranchiseMutation();
    const detachMutation = useDetachFranchiseMutation();

    const rows = data?.data ?? [];
    const meta = data?.meta;

    const closeAttach = () => {
        setAttaching(false);
        setPickedOem(null);
        setAttachError(null);
    };

    const handleAttach = () => {
        if (!pickedOem) return;
        setAttachError(null);
        attachMutation.mutate(
            { franchiseDealerId: dealerId, oemId: pickedOem.id },
            {
                onSuccess: () => {
                    toast.success(`${pickedOem.name} attached to ${dealerName}.`);
                    closeAttach();
                },
                onError: (err: unknown) => {
                    const msg = isApiError(err) ? err.message : 'Failed to attach OEM. Please try again.';
                    setAttachError(msg);
                    toast.error(msg);
                },
            },
        );
    };

    const handleDetach = (oemId: string) => {
        const oem = rows.find((r) => r.id === oemId);
        if (!window.confirm(`Detach ${oem?.name ?? 'this OEM'} from ${dealerName}?`)) return;

        setDetachingId(oemId);
        detachMutation.mutate(
            { franchiseDealerId: dealerId, oemId },
            {
                onSuccess: () => {
                    toast.success(`${oem?.name ?? 'OEM'} detached.`);
                    setDetachingId(null);
                },
                onError: (err: unknown) => {
                    const msg = isApiError(err) ? err.message : 'Failed to detach OEM. Please try again.';
                    toast.error(msg);
                    setDetachingId(null);
                },
            },
        );
    };

    if (isError) {
        return (
            <Alert variant="error">
                <span>Failed to load OEMs.</span>
            </Alert>
        );
    }

    return (
        <div className="space-y-6">
            <FranchiseMembersTable
                data={rows}
                isLoading={isLoading}
                onDetach={handleDetach}
                detachingId={detachingId}
                emptyMessage="No OEMs attached to this dealer yet."
            />

            {meta && meta.last_page > 1 && (
                <Pagination
                    currentPage={meta.current_page}
                    lastPage={meta.last_page}
                    total={meta.total}
                    perPage={meta.per_page}
                    onPageChange={setPage}
                    onPerPageChange={(next) => { setPerPage(next); setPage(1); }}
                />
            )}

            <div className="border-t border-mp-border pt-6 max-w-2xl">
                {attaching ? (
                    <div>
                        <h3 className="font-semibold mb-2">Attach {dealerName} to an OEM</h3>
                        <FranchisePartnerSelect
                            partnerType="oem"
                            value={pickedOem}
                            onChange={setPickedOem}
                            excludeIds={rows.map((r) => r.id)}
                            disabled={attachMutation.isPending}
                        />

                        {attachError && <p className="text-mp-error text-xs mt-1">{attachError}</p>}

                        <div className="flex justify-end gap-2 pt-3">
                            <Button type="button" variant="outline" size="sm" onClick={closeAttach} disabled={attachMutation.isPending}>
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                onClick={handleAttach}
                                disabled={!pickedOem || attachMutation.isPending}
                            >
                                {attachMutation.isPending ? 'Attaching...' : 'Attach'}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <h3 className="font-semibold mb-2">Attach an OEM</h3>
                        <div className="flex items-start gap-4 justify-between">
                            <p className="text-sm text-mp-muted">
                                Attach {dealerName} to an OEM it is franchised for. Only live (non-retired) OEMs can be attached.
                            </p>
                            <PermissionGuard permission={{ resource: 'dealer', action: 'edit' }}>
                                <Button type="button" size="sm" variant="primary" onClick={() => setAttaching(true)} className="whitespace-nowrap">
                                    Attach OEM
                                </Button>
                            </PermissionGuard>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
